import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentStaff, useStaffLogout, getStaffRedirectUrl } from '@/hooks/useStaffAuth';
import {
  useOrders,
  useRequestOrderCancellation,
  useUpdatePortionStatus,
  useUpdateOrderStatus,
} from '@/hooks/useOrders';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import { ClientNotificationsProvider } from '@/contexts/ClientNotificationsContext';
import { StaffBurgerMenu } from '@/components/layout/StaffBurgerMenu';
import { toast } from '@/hooks/use-toast';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock'
import { LogOut, Clock, CheckCircle, X, AlertCircle, Languages } from 'lucide-react';
import type { Order, OrderItem, OrderItemPortion } from '@/types/entities';

// Represents a kitchen card - a suborder with its items filtered by staff specialty
interface KitchenCard {
  id: string;
  subOrderId: string;
  parentOrderId: string;
  tableNumber: number;
  createdAt: string;
  status: 'pending' | 'in_progress' | 'ready';
  items: Array<{
    item: OrderItem;
    portions: OrderItemPortion[];
  }>;
  cancellationRequestedBy?: string | null | undefined;
  cancellationConfirmedBy?: string | null | undefined;
}

function KitchenDashboardContent() {
  const { t, i18n } = useTranslation();
  const { data: staff } = useCurrentStaff();
  const { mutate: logout } = useStaffLogout();
  const { data: allOrders, isLoading } = useOrders(staff?.restaurantId || null);
  const updatePortionStatus = useUpdatePortionStatus(staff?.restaurantId || '');
  const updateOrderStatus = useUpdateOrderStatus(staff?.restaurantId || '');
  const requestCancellation = useRequestOrderCancellation(staff?.restaurantId || '');
  const { data: specialties } = useKitchenSpecialties(staff?.restaurantId || null);
  // Android Chrome tablet: prevents screen sleep (reduces background throttling + socket stalls).
  useScreenWakeLock(true)

  // Get staff's specialty details
  const staffSpecialty = useMemo(() => {
    if (!staff?.specialtyId || !specialties) return null;
    return specialties.find((s) => s.id === staff.specialtyId);
  }, [staff?.specialtyId, specialties]);

  const handleLogout = async () => {
    await logout();
    // Redirect to staff login page with restaurant ID preserved
    window.location.href = getStaffRedirectUrl();
  };

  const handleMarkInProgress = async (card: KitchenCard) => {
    try {
      // Get all portions from items
      const allPortions = card.items.flatMap(({ portions }) => portions);

      if (allPortions.length > 0) {
        // Update all portions in this suborder (for this staff's specialty)
        const portionUpdates = allPortions.map((portion) =>
          updatePortionStatus.mutateAsync({
            portionId: portion.id,
            status: 'in_progress',
            staffId: staff?.id || '',
          })
        );
        await Promise.all(portionUpdates);
      } else {
        // No portions - update the sub-order status directly (for old orders or items without specialties)
        await updateOrderStatus.mutateAsync({
          orderId: card.subOrderId,
          status: 'in_progress',
        });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: t('common.error'),
        description: t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleMarkReady = async (card: KitchenCard) => {
    try {
      // Get all portions from items
      const allPortions = card.items.flatMap(({ portions }) => portions);

      if (allPortions.length > 0) {
        // Update all portions in this suborder (for this staff's specialty)
        const portionUpdates = allPortions.map((portion) =>
          updatePortionStatus.mutateAsync({
            portionId: portion.id,
            status: 'ready',
            staffId: staff?.id || '',
          })
        );
        await Promise.all(portionUpdates);
      } else {
        // No portions - update the sub-order status directly (for old orders or items without specialties)
        await updateOrderStatus.mutateAsync({
          orderId: card.subOrderId,
          status: 'ready',
        });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: t('common.error'),
        description: t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleCancelOrder = async (orderId: string, order: Order) => {
    try {
      // Check if cancellation was already requested
      if (order.cancellationRequestedBy && !order.cancellationConfirmedBy) {
        // Already requested, waiting for admin approval
        toast({
          title: t('common.info'),
          description: t('order.waitingForAdminApproval'),
          variant: 'default',
        });
      } else {
        // Request cancellation (admin will approve/reject)
        await requestCancellation.mutateAsync({
          orderId,
          staffId: staff?.id || '',
        });
        toast({
          title: t('common.success'),
          description: t('order.cancellationRequested'),
        });
      }
    } catch (error: any) {
      console.error('Failed to cancel order:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  // Extract kitchen cards from orders - group by suborder
  const kitchenCards = useMemo(() => {
    if (!allOrders || allOrders.length === 0) {
      return [];
    }
    const cards: KitchenCard[] = [];
    const staffSpecialtyId = staff?.specialtyId || null;

    allOrders.forEach((parentOrder) => {
      // Only process active parent orders
      if (
        parentOrder.status !== 'completed' &&
        parentOrder.status !== 'cancelled' &&
        (parentOrder.status as string) !== 'checked'
      ) {
        // Process all sub-orders
        if (parentOrder.subOrders && parentOrder.subOrders.length > 0) {
          parentOrder.subOrders.forEach((subOrder) => {
            // Skip cancelled sub-orders
            if (subOrder.status === 'cancelled') return;

            const tableNumber = subOrder.table?.tableNumber || parentOrder.table?.tableNumber || 0;
            const items = subOrder.items || [];

            // Collect items and portions relevant to this staff's specialty
            const relevantItems: Array<{ item: OrderItem; portions: OrderItemPortion[] }> = [];

            items.forEach((item) => {
              const itemPortions = item.portions || [];
              const itemSpecialties = item.specialtyIds || [];

              if (itemPortions.length > 0) {
                // Item has portions - filter by staff specialty
                const relevantPortions = staffSpecialtyId
                  ? itemPortions.filter((p) => p.specialtyId === staffSpecialtyId)
                  : itemPortions;

                if (relevantPortions.length > 0) {
                  relevantItems.push({
                    item,
                    portions: relevantPortions,
                  });
                }
              } else if (itemSpecialties.length === 0) {
                // Item has no specialties - visible to all kitchen staff
                relevantItems.push({
                  item,
                  portions: [],
                });
              } else {
                // Item has specialties but no portions (old orders)
                if (!staffSpecialtyId || itemSpecialties.includes(staffSpecialtyId)) {
                  relevantItems.push({
                    item,
                    portions: [],
                  });
                }
              }
            });

            // Only create a card if there are relevant items for this staff
            if (relevantItems.length > 0) {
              // Calculate status based on portions or suborder status
              let cardStatus: 'pending' | 'in_progress' | 'ready' = subOrder.status as any;

              // If there are portions, determine status from them
              // Status logic: pending > in_progress > ready (same as backend)
              const allPortions = relevantItems.flatMap(({ portions }) => portions);
              if (allPortions.length > 0) {
                const hasPending = allPortions.some((p) => p.status === 'pending');
                const hasInProgress = allPortions.some((p) => p.status === 'in_progress');
                const allReady = allPortions.every((p) => p.status === 'ready');

                if (allReady) {
                  cardStatus = 'ready';
                } else if (hasPending) {
                  cardStatus = 'pending';
                } else if (hasInProgress) {
                  cardStatus = 'in_progress';
                }
              }

              cards.push({
                id: `suborder-${subOrder.id}`,
                subOrderId: subOrder.id,
                parentOrderId: subOrder.parentOrderId || parentOrder.id,
                tableNumber,
                createdAt: subOrder.createdAt,
                status: cardStatus,
                items: relevantItems,
                cancellationRequestedBy: subOrder.cancellationRequestedBy,
                cancellationConfirmedBy: subOrder.cancellationConfirmedBy,
              });
            }
          });
        }
      }
    });

    return cards;
  }, [allOrders, staff?.specialtyId]);

  // Separate cards by status
  const readyCards = kitchenCards.filter((c) => c.status === 'ready');
  const inProgressCards = kitchenCards.filter((c) => c.status === 'in_progress');
  const pendingCards = kitchenCards.filter((c) => c.status === 'pending');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'in_progress':
        return 'bg-blue-500';
      case 'ready':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const renderKitchenCard = (card: KitchenCard) => {
    const isArabic = i18n.language === 'ar';
    const { tableNumber, createdAt, status, subOrderId, items } = card;
    const hasCancellationPending = card.cancellationRequestedBy && !card.cancellationConfirmedBy;

    return (
      <Card key={card.id} className="mb-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tables.tableNumber', { number: tableNumber })}</CardTitle>
              <CardDescription>
                {new Date(createdAt).toLocaleTimeString()}
                <span className="ml-2 text-xs">(#{subOrderId.slice(0, 8)})</span>
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(status)}>{t(`orders.status.${status}`)}</Badge>
              {hasCancellationPending && (
                <Badge className="bg-orange-500 animate-pulse">
                  {t('order.cancellationPending')}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Items List */}
          <div className="space-y-2 mb-4">
            {items.map(({ item, portions }) => {
              // Group portions by specialty
              const portionsBySpecialty = portions.reduce(
                (acc, portion) => {
                  const key = portion.specialtyId || 'no-specialty';
                  if (!acc[key]) {
                    acc[key] = {
                      portions: [],
                      specialty: {
                        id: portion.specialtyId,
                        nameAr: portion.specialtyNameAr,
                        nameFr: portion.specialtyNameFr,
                        color: portion.specialtyColor,
                      },
                    };
                  }
                  acc[key].portions.push(portion);
                  return acc;
                },
                {} as Record<string, { portions: OrderItemPortion[]; specialty: any }>
              );

              return (
                <div
                  key={item.id}
                  className="flex justify-between items-start p-2 bg-muted rounded"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">
                        {isArabic ? item.menuItem?.nameAr : item.menuItem?.nameFr}
                      </p>
                      {/* Show specialty badges for each portion group */}
                      {Object.values(portionsBySpecialty).map(
                        ({ specialty }) =>
                          specialty.id && (
                            <Badge
                              key={specialty.id}
                              style={{ backgroundColor: specialty.color || '#888' }}
                              className="text-white text-xs"
                            >
                              {isArabic ? specialty.nameAr : specialty.nameFr}
                            </Badge>
                          )
                      )}
                    </div>
                    {item.menuItemOption && (
                      <p className="text-sm text-primary font-medium">
                        {isArabic ? item.menuItemOption.nameAr : item.menuItemOption.nameFr}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{item.notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="ml-2">
                      x{item.quantity}
                    </Badge>
                    <p className="text-sm font-medium mt-1">
                      {((item.priceAtOrder || item.menuItem?.price || 0) * item.quantity).toFixed(
                        2
                      )}{' '}
                      DH
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {status === 'pending' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleMarkInProgress(card)}
                  disabled={updatePortionStatus.isPending || updateOrderStatus.isPending}
                  className="flex-1"
                >
                  <Clock className="w-4 h-4 me-2" />
                  {t('orders.markInProgress')}
                </Button>
                <Button
                  size="sm"
                  variant={hasCancellationPending ? 'default' : 'destructive'}
                  onClick={() =>
                    handleCancelOrder(subOrderId, {
                      id: subOrderId,
                      cancellationRequestedBy: card.cancellationRequestedBy,
                      cancellationConfirmedBy: card.cancellationConfirmedBy,
                    } as Order)
                  }
                  disabled={requestCancellation.isPending || !!hasCancellationPending}
                  className={hasCancellationPending ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  {hasCancellationPending ? (
                    <>
                      <AlertCircle className="w-4 h-4 me-2" />
                      {t('order.pendingApproval')}
                    </>
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </Button>
              </>
            )}
            {status === 'in_progress' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleMarkReady(card)}
                  disabled={updatePortionStatus.isPending || updateOrderStatus.isPending}
                  className="flex-1"
                >
                  <CheckCircle className="w-4 h-4 me-2" />
                  {t('orders.markReady')}
                </Button>
                <Button
                  size="sm"
                  variant={hasCancellationPending ? 'default' : 'destructive'}
                  onClick={() =>
                    handleCancelOrder(subOrderId, {
                      id: subOrderId,
                      cancellationRequestedBy: card.cancellationRequestedBy,
                      cancellationConfirmedBy: card.cancellationConfirmedBy,
                    } as Order)
                  }
                  disabled={requestCancellation.isPending || !!hasCancellationPending}
                  className={hasCancellationPending ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  {hasCancellationPending ? (
                    <>
                      <AlertCircle className="w-4 h-4 me-2" />
                      {t('order.pendingApproval')}
                    </>
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </Button>
              </>
            )}
            {status === 'ready' && hasCancellationPending && (
              <Button
                size="sm"
                variant="default"
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled
              >
                <AlertCircle className="w-4 h-4 me-2" />
                {t('order.pendingApproval')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!staff) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold">{t('staff.kitchenDashboard')}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {t('common.welcome')}, {staff.username}
              {staffSpecialty && (
                <Badge className="ms-2" style={{ backgroundColor: staffSpecialty.color }}>
                  {i18n.language === 'ar' ? staffSpecialty.nameAr : staffSpecialty.nameFr}
                </Badge>
              )}
            </p>
          </div>
          {/* Mobile Burger Menu */}
          <div className="md:hidden">
            <StaffBurgerMenu onLogout={handleLogout} />
          </div>
          {/* Desktop Language & Logout */}
          <div className="hidden md:flex items-center gap-2">
            <Select value={i18n.language} onValueChange={(lang) => i18n.changeLanguage(lang)}>
              <SelectTrigger className="w-[140px]">
                <Languages className="w-4 h-4 me-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 me-2" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Orders by Status */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Pending Items/Portions - En attente (Left) */}
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Badge className="bg-yellow-500 me-2">{pendingCards.length}</Badge>
                {t('orders.pending')}
              </h2>
              <div>{pendingCards.map(renderKitchenCard)}</div>
            </div>

            {/* In Progress Items/Portions - En cours (Middle) */}
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Badge className="bg-blue-500 me-2">{inProgressCards.length}</Badge>
                {t('orders.inProgress')}
              </h2>
              <div>{inProgressCards.map(renderKitchenCard)}</div>
            </div>

            {/* Ready Items/Portions - Prêt (Right) */}
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Badge className="bg-green-500 me-2">{readyCards.length}</Badge>
                {t('orders.ready')}
              </h2>
              <div>{readyCards.map(renderKitchenCard)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function KitchenDashboard() {
  const { data: staff, isLoading } = useCurrentStaff();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!staff) {
    return null;
  }

  return (
    <ClientNotificationsProvider staff={staff} restaurantId={staff.restaurantId}>
      <KitchenDashboardContent />
    </ClientNotificationsProvider>
  );
}
